/**
 * FavoritesTab — Starred tools & presets for the sidebar toolbar.
 */

import { Star } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLayoutStore } from '@/store/useLayoutStore';
import type { FavoriteItem } from '@/types/toolBrowser';

import { PresetRow } from './PresetRow';
import { ToolRow } from './ToolRow';

export interface FavoritesTabProps {
  filteredFavoriteItems: FavoriteItem[];
  selectedToolId: string | null;
  selectedPresetId: string | null;
  recommendationMap: Map<string, { score: number; reason: string }>;
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  onSelectTool: (toolId: string) => void;
  onSelectPreset: (presetId: string, toolId: string) => void;
  onAddToolToPipeline: (toolId: string) => void;
  onAddPresetToPipeline: (
    presetId: string,
    toolId: string,
    config: Record<string, unknown>,
    name: string,
  ) => void;
}

export const FavoritesTab: React.FC<FavoritesTabProps> = ({
  filteredFavoriteItems,
  selectedToolId,
  selectedPresetId,
  recommendationMap,
  isFavorite,
  onToggleFavorite,
  onSelectTool,
  onSelectPreset,
  onAddToolToPipeline,
  onAddPresetToPipeline,
}) => {
  const { t } = useTranslation('sidebar');
  const closeDrawer = useLayoutStore((s) => s.closeDrawer);

  if (filteredFavoriteItems.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
        <Star className="h-8 w-8 text-muted-foreground/40" />
        <div className="text-sm text-muted-foreground">{t('favorites.empty')}</div>
        <div className="text-xs text-muted-foreground/70">
          {t('favorites.emptyHint')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-2 pb-4">
      <div className="p-1 space-y-1">
        {filteredFavoriteItems.map((item) => {
          if (item.kind === 'tool') {
            const rec = recommendationMap.get(item.tool.id);
            return (
              <ToolRow
                key={item.tool.id}
                tool={item.tool}
                selected={item.tool.id === selectedToolId && !selectedPresetId}
                isFavorite={isFavorite(item.tool.id)}
                onToggleFavorite={() => onToggleFavorite(item.tool.id)}
                isRecommended={!!rec}
                recommendedReason={rec?.reason}
                onSelect={() => { onSelectTool(item.tool.id); closeDrawer(); }}
                onAddToPipeline={() => { onAddToolToPipeline(item.tool.id); closeDrawer(); }}
              />
            );
          }
          const { preset, tool: baseTool } = item;
          const rec = recommendationMap.get(preset.toolId);
          return (
            <PresetRow
              key={preset.id}
              presetName={preset.name}
              presetId={preset.id}
              toolName={baseTool?.name ?? preset.toolId}
              toolDescription={baseTool?.description ?? ''}
              selected={selectedPresetId === preset.id}
              isFavorite={isFavorite(preset.id)}
              isRecommended={!!rec}
              recommendedReason={rec?.reason}
              onToggleFavorite={() => onToggleFavorite(preset.id)}
              onSelect={() => { onSelectPreset(preset.id, preset.toolId); closeDrawer(); }}
              onAddToPipeline={() => {
                onAddPresetToPipeline(preset.id, preset.toolId, preset.config, preset.name);
                closeDrawer();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
